import { useUser } from 'contexts/userContext';
import { useMemo } from 'react';

import { NON_MANDATORY_STEPS, summarizeSteps } from 'utils/profileSteps';

/**
 * Check if there are mandatory steps pending in the user's profile.
 *
 * This hook checks the `missingSteps` object in the `useUser` context,
 * filters out non-mandatory fields, and determines if any required step is incomplete.
 *
 * @returns {boolean} - Returns `true` if there are any mandatory steps missing, otherwise `false`.
 */
const useMissingSteps = () => {
  const { whoAmI } = useUser();

  const hasMissingSteps = useMemo(() => {
    if (!whoAmI?.missingSteps) return false;

    const { missingSteps } = whoAmI;

    const mandatoryStepKeys = Object.keys(missingSteps).filter(
      (stepKey) => !NON_MANDATORY_STEPS.includes(stepKey)
    );

    const hasPendingMandatoryStep = mandatoryStepKeys.some(
      (stepKey) => missingSteps[stepKey] === false
    );

    return hasPendingMandatoryStep;
  }, [whoAmI]);

  return hasMissingSteps;
};

/** Progress across the advisor's mandatory onboarding steps. */
export const useProfileProgress = () => {
  const { whoAmI } = useUser();

  return useMemo(() => summarizeSteps(whoAmI?.missingSteps), [whoAmI]);
};

export default useMissingSteps;
